import {
  AlertOutlined,
  PhoneOutlined,
  RiseOutlined,
  StarOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import Audience from '@components/audience/Audience'
import { Audience as AudienceModel } from '@models/Audience'
import { Segment as SegmentModel } from '@models/Segment'
import { useAppSelector } from '@store/hooks'
import { selectSegmentById } from '@store/SegmentReducer'
import { SBIcon } from '@supplement-bacon/react-toolkit/components'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Card, Flex, Space, Typography } from 'antd'
import { FC } from 'react'

const { Title, Text } = Typography

const icons = {
  UserOutlined: <UserOutlined />,
  AlertOutlined: <AlertOutlined />,
  StarOutlined: <StarOutlined />,
  RiseOutlined: <RiseOutlined />,
  TeamOutlined: <TeamOutlined />,
  PhoneOutlined: <PhoneOutlined />,
}

type Props = SBModelComponent<SegmentModel> & {}

/**
 * Segment row of the Segments page.
 *
 * Update/delete are offered on custom segments only: native audiences are
 * seeded server-side and the API answers 403 on either action.
 */
const SegmentCard: FC<Props> = ({ id, model }) => {
  const item = model ?? useAppSelector(selectSegmentById(id!))

  if (!item) {
    return <></>
  }

  return (
    <Card>
      <Flex align="center" justify="space-between" gap={20}>
        <Space size={20}>
          {item.icon && (
            <Text style={{ fontSize: 20 }}>
              {/* Native audiences use a fixed Ant Design icon; custom segments
                  use whatever the IconPicker stored (rendered via SBIcon). */}
              {icons[item.icon as keyof typeof icons] ?? (
                <SBIcon name={item.icon} />
              )}
            </Text>
          )}
          <Space orientation="vertical" size={0}>
            <Title level={5} className="m-0">
              {item.title}
            </Title>
            <Text>{item.description}</Text>
          </Space>
        </Space>

        <Space size={12}>
          {/* A Segment carries the audience count fields it is rendered from. */}
          <Audience.UsersCount audience={item as AudienceModel} />
          {item.is_custom && (
            <SBRM.Actions
              entity={Entity.segment}
              entityId={item.id}
              actions={[SBRMAction.Update, SBRMAction.Delete]}
            />
          )}
        </Space>
      </Flex>
    </Card>
  )
}

type SegmentCardType = { Card: typeof SegmentCard }

export { SegmentCard, type SegmentCardType }
